import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Branding, CompanyProfile, TenantSettings } from '@goyone/shared';
import { ErrorState, Notice } from '../../../components/ui';
import { Icon } from '../../../components/Icon';
import { useToast } from '../../../components/Toast';
import { api } from '../../../lib/api';
import { useAuth } from '../../../auth/AuthProvider';

interface Profile {
  tenant: { id: string; slug: string; name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

/**
 * عنوانُ الشركة وعملتُها.
 *
 * ## ولماذا هي هنا وهي في «إعدادات الشركة» أيضًا
 *
 * ليست نسخةً: الحقولُ هي الحقولُ نفسُها، والمسارُ هو المسارُ نفسُه، وما
 * يُحفظ من هنا يظهر هناك في اللحظة نفسِها. وإنّما أُحضرت لأنّ من يُعدّ
 * المؤسسةَ يجلس جلسةً واحدةً يضبط فيها العنوانَ والعملةَ والمواقعَ وخطط
 * العمل — وإرسالُه إلى قائمةٍ أخرى في منتصف العمل يجعله يترك نصفَ الإعداد
 * لغدٍ لا يأتي.
 *
 * والعملةُ تُقرأ من الإعدادات لا من صفّ المستأجر: صفُّ المستأجر يحمل
 * عملةَ الاشتراك في المنصّة، والإعداداتُ تحمل عملةَ الشركة في مستنداتها —
 * وهما تتفقان غالبًا وتفترقان أحيانًا.
 */
export function CompanyBasicsSection() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();

  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<Profile>('/settings'),
  });

  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [currency, setCurrency] = useState('');
  const [currencyLabel, setCurrencyLabel] = useState('');

  /*
   * الحقولُ تُملأ حين يصل الملفّ لا قبله.
   *
   * وقيمةُ البداية وحدَها لا تكفي: الاستعلامُ يردّ بعد أوّل رسم، فلو لم
   * تُنسخ القيمُ هنا لبقيت الخانات فارغةً ولبدا للمستخدم أنّ الشركةَ بلا
   * عنوان — فيكتبه من جديد ويمحو ما كان.
   */
  useEffect(() => {
    const data = profile.data;
    if (!data) return;
    setName(data.tenant.name);
    setLegalName(data.company.legalName);
    setTaxNumber(data.company.taxNumber);
    setPhone(data.company.phone);
    setEmail(data.company.email);
    setAddress(data.company.address);
    setCurrency(data.settings.currency);
    setCurrencyLabel(data.settings.currencyLabel);
  }, [profile.data]);

  const save = useMutation({
    mutationFn: async () => {
      /*
       * نداءان لأنّ الوجهتين مختلفتان: البياناتُ في ملفّ الشركة والعملةُ في
       * الإعدادات التشغيلية. ويُنتظر الأوّلُ قبل الثاني لا يُرسلان معًا:
       * فشلُ أحدهما بعد نجاح الآخر يجب أن يُقرأ في رسالةٍ واحدة.
       */
      await api('/settings/company', {
        method: 'PATCH',
        body: {
          name: name.trim(),
          legalName: legalName.trim(),
          taxNumber: taxNumber.trim(),
          phone: phone.trim(),
          email: email.trim(),
          address: address.trim(),
        },
      });
      await api('/settings/preferences', {
        method: 'PATCH',
        body: {
          currency: currency.trim() || 'EGP',
          currencyLabel: currencyLabel.trim() || 'ج.م',
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast('حُفظت بيانات الشركة');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  if (profile.isError) {
    return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  }

  if (profile.isPending) {
    return <div className="skeleton" style={{ height: 260 }} />;
  }

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h2 className="card-title">بيانات الشركة</h2>
          <span className="tiny mute">
            الاسمُ والعنوانُ والعملة — تُقرأ في المستندات وفي كشف المرتّبات
          </span>
        </div>

        <div className="card-body">
          <div className="form-grid">
            <div className="field span-2">
              <label className="label req" htmlFor="co-name">
                اسم الشركة
              </label>
              <input
                id="co-name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit}
                maxLength={160}
              />
              <span className="tiny mute">
                هو ما يظهر في ترويسة المستندات وفي عروض العمل التي تُرسل
                للمرشّحين.
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="co-legal">
                الاسم القانوني
              </label>
              <input
                id="co-legal"
                className="input"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                disabled={!canEdit}
                maxLength={200}
                placeholder="شركة … ش.م.م"
              />
              <span className="tiny mute">
                ما تُوقَّع به العقودُ إن كان يختلف عن الاسم التجاريّ.
              </span>
            </div>

            <div className="field">
              <label className="label" htmlFor="co-tax">
                الرقم الضريبي
              </label>
              <input
                id="co-tax"
                className="input num"
                dir="ltr"
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value)}
                disabled={!canEdit}
                maxLength={40}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="co-phone">
                الهاتف
              </label>
              <input
                id="co-phone"
                className="input num"
                dir="ltr"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={!canEdit}
                maxLength={40}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="co-email">
                البريد
              </label>
              <input
                id="co-email"
                className="input"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!canEdit}
                maxLength={160}
              />
            </div>

            <div className="field span-2">
              <label className="label" htmlFor="co-address">
                العنوان
              </label>
              <input
                id="co-address"
                className="input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={!canEdit}
                maxLength={400}
                placeholder="٢٧ شارع النصر، المعادي، القاهرة"
              />
              {/*
                العنوانُ نصٌّ يُطبع، والموقعُ نقطةٌ يُقاس عليها الحضور. وخلطُهما
                يجعل من يكتب العنوانَ يظنّ أنّه ضبط نطاقَ الحضور.
              */}
              <span className="tiny mute">
                عنوانٌ يُطبع في المستندات. ونطاقُ الحضور لا يُقاس عليه —
                يُضبط في «المواقع» بنقطةٍ ونصف قطر.
              </span>
            </div>

            <div className="field">
              <label className="label req" htmlFor="co-currency">
                رمز العملة
              </label>
              <input
                id="co-currency"
                className="input"
                dir="ltr"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={!canEdit}
                maxLength={10}
                placeholder="EGP"
              />
              <span className="tiny mute">ثلاثةُ أحرفٍ بالمعيار الدولي.</span>
            </div>

            <div className="field">
              <label className="label req" htmlFor="co-currency-label">
                ما يُكتب بجانب المبالغ
              </label>
              <input
                id="co-currency-label"
                className="input"
                value={currencyLabel}
                onChange={(e) => setCurrencyLabel(e.target.value)}
                disabled={!canEdit}
                maxLength={10}
                placeholder="ج.م"
              />
              <span className="tiny mute">
                يظهر في الفواتير وقسائم المرتّبات وحدود رواتب الوظائف.
              </span>
            </div>
          </div>

          {canEdit && (
            <div className="row mt-4" style={{ gap: 'var(--sp-3)' }}>
              <button
                className="btn btn-primary"
                onClick={() => save.mutate()}
                disabled={save.isPending || name.trim().length < 2}
              >
                {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
              </button>
              {save.isError && (
                <span className="tiny" style={{ color: 'var(--danger)' }}>
                  {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/*
        الحقولُ واحدةٌ في المكانين، ويُقال ذلك صراحةً: من يجدها هنا وهناك
        بلا بيانٍ يظنّ أنّ لكلٍّ نسختَه فيحتار أيَّهما يُصدَّق.
      */}
      <Notice title="هذه الحقول نفسُها في إعدادات الشركة" icon="info">
        ليست نسخةً ثانية: ما يُحفظ هنا يظهر في «الإعدادات ← إعدادات الشركة»
        فورًا، والعكس. وأُحضرت إلى هنا لأنّ من يضبط المؤسسةَ يضبط عنوانَها
        وعملتَها ومواقعَها وخططَ عملها في جلسةٍ واحدة. أمّا الهويةُ البصرية
        وشروطُ المستندات والضريبةُ على الفواتير فتبقى هناك — ولا شأنَ لها
        بالموارد البشرية.{' '}
        <span className="row" style={{ gap: 4, display: 'inline-flex' }}>
          <Icon name="settings" size={13} />
        </span>
      </Notice>
    </>
  );
}
